import { useCallback, useEffect, useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { makeStyles, Typography } from '@material-ui/core';
import PageContainer from 'components/shared/pageContainer';
import CustomButton from 'components/shared/customButton';
import CustomFormDrawer from 'components/shared/customFormDrawer';
import DotsMenu from 'components/shared/dotsMenu';
import NumberStepper from 'components/shared/numberStepper';
import SectionTitle from 'components/shared/sectionTitle';
import SuccessionSettingsSection from 'components/shared/successionSettingsSection';
import { sticky } from 'constants/helperCssRules';
import { APP_PAGES } from 'constants/pages';
import { SUCCESSION_SETTINGS_FIELD_NAMES } from 'constants/succession';
import { successionSettingsSelector } from 'store/selectors/successionSettingsSelector';
import {
  clearSuccessionSettings,
  getAllSuccessionSettings,
  updateSuccessionCoverageRatio,
  reorderSuccessionCatalog,
  saveSuccessionSetting,
  setSuccessionSettings,
} from 'store/modules/successionSettings';
import { prepareSuccessionNavigationItems } from 'utility/succession';
import { showSuccessMessage } from 'utility/uiUtils';
import { useTranslations } from 'utility/useTranslations';
import {
  applyDrawerValues,
  COVERAGE_FIELD,
  getActionColumn,
  getCriticalityHeaders,
  getDrawerInitialData,
  getJobFamilyHeaders,
  getReadinessHeaders,
  getRemovalReasonHeaders,
  isUserRemovalReason,
  moveOrderedItem,
  prepareCriticalityRows,
  prepareEditMenuItems,
  prepareJobFamilyRows,
  prepareLabelFields,
  prepareNameFields,
  prepareOrderedMenuItems,
  prepareReadinessRows,
  prepareRemovalReasonRows,
  SETTINGS_DRAWER_TRANSLATION_KEYS,
  validateCoverageRatio,
} from './config';

const {
  READINESS,
  CRITICALITY,
  REMOVAL_REASONS,
  JOB_FAMILIES,
  GENERIC_COVERAGE_RATIO,
} = SUCCESSION_SETTINGS_FIELD_NAMES;

const drawerCatalogItem = (nextSettings, listKey, item) => {
  const nextList = nextSettings[listKey] || [];

  if (!item?.id) return nextList[nextList.length - 1];

  return nextList.find(entry => entry.id === item.id);
};

const useStyles = makeStyles(
  ({ breakpoints, palette: { primary }, spacing }) => ({
    description: {
      padding: spacing(2, 0, 6, 0),
      ...sticky(primary.white, 170),
    },
    sections: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr)',
      columnGap: spacing(8),
      rowGap: spacing(8),
      [breakpoints.up('md')]: {
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
      },
    },
    section: {
      minWidth: 0,
    },
    coverage: {
      [breakpoints.up('md')]: {
        gridColumn: '1 / -1',
      },
    },
    sectionDescription: {
      marginBottom: spacing(3),
      color: primary.bluish4,
    },
    coverageTitle: {
      marginBottom: spacing(2),
    },
    coverageRule: {
      marginBottom: spacing(2),
    },
    coverageControls: {
      display: 'flex',
      alignItems: 'flex-end',
      gap: spacing(4),
      marginTop: spacing(3),
    },
  })
);

const SuccessionSettingsPage = ({ accessControl, dispatch, ...rest }) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SUCCESSION_SETTINGS);
  const settings = useSelector(successionSettingsSelector);

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [coverageDirty, setCoverageDirty] = useState(false);
  const [coverageError, setCoverageError] = useState(false);

  const navigationItems = useMemo(
    () => prepareSuccessionNavigationItems(translations.navigationItems),
    [translations.navigationItems]
  );

  const getInitialData = useCallback(async () => {
    try {
      await getAllSuccessionSettings(dispatch);
    } finally {
      setIsInitialLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    getInitialData();
  }, [getInitialData]);

  const cleanup = useCallback(() => {
    dispatch(clearSuccessionSettings());
  }, [dispatch]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const runSave = useCallback(async (action, successMessage) => {
    await action();
    showSuccessMessage(successMessage);
  }, []);

  const handleOpenDrawer = useCallback((listKey, item = null) => {
    setActiveDrawer({ listKey, item });
  }, []);

  const getOpenDrawerHandler = useCallback(
    (listKey, item = null) =>
      () =>
        handleOpenDrawer(listKey, item),
    [handleOpenDrawer]
  );

  const handleCloseDrawer = () => {
    setActiveDrawer(null);
  };

  const drawerSuccessMessage = (listKey, isEdit) => {
    const messages = translations.successMessages;

    if (listKey === READINESS) {
      return isEdit ? messages.readinessUpdated : messages.readinessSaved;
    }
    if (listKey === CRITICALITY) {
      return isEdit ? messages.criticalityUpdated : messages.criticalitySaved;
    }
    if (listKey === REMOVAL_REASONS) {
      return isEdit
        ? messages.removalReasonUpdated
        : messages.removalReasonSaved;
    }

    return isEdit ? messages.jobFamilyUpdated : messages.jobFamilySaved;
  };

  const handleSaveDrawer = async values => {
    if (!settings || !activeDrawer) return { shouldResetForm: false };

    const { listKey, item } = activeDrawer;
    const isEdit = Boolean(item?.id);
    const nextItem = drawerCatalogItem(
      applyDrawerValues(settings, listKey, item, values),
      listKey,
      item
    );
    if (!nextItem) return { shouldResetForm: false };

    await runSave(
      () => saveSuccessionSetting(dispatch, listKey, nextItem),
      drawerSuccessMessage(listKey, isEdit)
    );
  };

  const handleMoveOrdered = async (
    listKey,
    item,
    direction,
    successMessage
  ) => {
    if (!settings) return;

    const currentList = settings[listKey];
    const nextList = moveOrderedItem(currentList, item.id, direction);
    if (nextList === currentList) return;

    await runSave(
      () =>
        reorderSuccessionCatalog(
          dispatch,
          listKey,
          nextList.map(entry => entry.id)
        ),
      successMessage
    );
  };

  const handleCoverageChange = nextValue => {
    setCoverageError(!validateCoverageRatio(nextValue));
    setCoverageDirty(true);
    dispatch(
      setSuccessionSettings({
        ...settings,
        [GENERIC_COVERAGE_RATIO]: nextValue,
      })
    );
  };

  const handleSaveCoverage = async () => {
    const ratio = settings[GENERIC_COVERAGE_RATIO];
    if (!validateCoverageRatio(ratio)) {
      setCoverageError(true);
      return;
    }

    setCoverageError(false);
    await runSave(
      () => updateSuccessionCoverageRatio(dispatch, Number(ratio)),
      translations.coverage.saved
    );
    setCoverageDirty(false);
  };

  const actionColumn = useMemo(
    () => getActionColumn(translations.columns),
    [translations.columns]
  );
  const rowLabels = translations;
  const menuLabels = translations.menu;

  const renderOrderedActions = (listKey, item, index, count) => {
    const successMessage =
      listKey === READINESS
        ? translations.successMessages.readinessReordered
        : translations.successMessages.criticalityReordered;

    return (
      <DotsMenu
        isVerticalMenu
        menuItems={prepareOrderedMenuItems(
          menuLabels,
          item,
          index,
          count,
          getOpenDrawerHandler(listKey, item),
          selected =>
            handleMoveOrdered(listKey, selected, 'up', successMessage),
          selected =>
            handleMoveOrdered(listKey, selected, 'down', successMessage)
        )}
      />
    );
  };

  const renderEditActions = (listKey, item) => (
    <DotsMenu
      isVerticalMenu
      menuItems={prepareEditMenuItems(
        menuLabels,
        getOpenDrawerHandler(listKey, item)
      )}
    />
  );

  const renderRemovalActions = item => {
    if (!isUserRemovalReason(item.used_by)) return null;

    return renderEditActions(REMOVAL_REASONS, item);
  };

  const readinessRows = settings
    ? prepareReadinessRows(
        settings.readiness_levels,
        rowLabels,
        (item, index, count) =>
          renderOrderedActions(READINESS, item, index, count)
      )
    : [];
  const criticalityRows = settings
    ? prepareCriticalityRows(
        settings.criticality_levels,
        rowLabels,
        (item, index, count) =>
          renderOrderedActions(CRITICALITY, item, index, count)
      )
    : [];
  const removalRows = settings
    ? prepareRemovalReasonRows(
        settings.removal_reasons,
        rowLabels,
        renderRemovalActions
      )
    : [];
  const familyRows = settings
    ? prepareJobFamilyRows(settings.job_families, rowLabels, item =>
        renderEditActions(JOB_FAMILIES, item)
      )
    : [];

  const drawerKey = activeDrawer
    ? SETTINGS_DRAWER_TRANSLATION_KEYS[activeDrawer.listKey]
    : 'readiness';
  const drawerFields =
    activeDrawer?.listKey === JOB_FAMILIES
      ? prepareNameFields()
      : prepareLabelFields();
  const coverageRatio = Number(settings?.[GENERIC_COVERAGE_RATIO]);

  return (
    <PageContainer
      {...rest}
      accessControl={accessControl}
      dispatch={dispatch}
      translations={translations}
      navigationItems={navigationItems}
      shouldPassProps={false}
      isFullWidthContent
    >
      <Typography className={classes.description} variant="body2">
        {translations.description}
      </Typography>
      {!isInitialLoading && (
        <div className={classes.sections}>
          <SuccessionSettingsSection
            translations={translations}
            title={translations.sections.readiness.title}
            description={translations.sections.readiness.description}
            addLabel={translations.sections.readiness.add}
            noDataLabel={translations.sections.readiness.empty}
            headers={getReadinessHeaders(translations.columns)}
            headerActions={actionColumn}
            rows={readinessRows}
            onAdd={getOpenDrawerHandler(READINESS)}
          />
          <SuccessionSettingsSection
            translations={translations}
            title={translations.sections.criticality.title}
            description={translations.sections.criticality.description}
            addLabel={translations.sections.criticality.add}
            noDataLabel={translations.sections.criticality.empty}
            headers={getCriticalityHeaders(translations.columns)}
            headerActions={actionColumn}
            rows={criticalityRows}
            onAdd={getOpenDrawerHandler(CRITICALITY)}
          />
          <SuccessionSettingsSection
            translations={translations}
            title={translations.sections.removalReasons.title}
            description={translations.sections.removalReasons.description}
            addLabel={translations.sections.removalReasons.add}
            noDataLabel={translations.sections.removalReasons.empty}
            headers={getRemovalReasonHeaders(translations.columns)}
            headerActions={actionColumn}
            rows={removalRows}
            onAdd={getOpenDrawerHandler(REMOVAL_REASONS)}
          />
          <SuccessionSettingsSection
            translations={translations}
            title={translations.sections.jobFamilies.title}
            description={translations.sections.jobFamilies.description}
            addLabel={translations.sections.jobFamilies.add}
            noDataLabel={translations.sections.jobFamilies.empty}
            headers={getJobFamilyHeaders(translations.columns)}
            headerActions={actionColumn}
            rows={familyRows}
            onAdd={getOpenDrawerHandler(JOB_FAMILIES)}
          />
          <section className={`${classes.section} ${classes.coverage}`}>
            <SectionTitle
              className={classes.coverageTitle}
              title={translations.coverage.title}
              variant="h5"
            />
            <Typography className={classes.coverageRule} variant="body2">
              {translations.coverage.genericRule}
            </Typography>
            <Typography className={classes.sectionDescription} variant="body2">
              {translations.coverage.seatRule}
            </Typography>
            <div className={classes.coverageControls}>
              <NumberStepper
                translations={translations.coverage}
                field={COVERAGE_FIELD}
                value={coverageRatio}
                hasError={coverageError}
                errorMessage={
                  coverageError
                    ? translations.coverage.validationMessages.minValue
                    : ''
                }
                onChange={handleCoverageChange}
              />
              <CustomButton
                type="withTextDarkRounded"
                onClick={handleSaveCoverage}
                disabled={!coverageDirty || coverageError}
              >
                {translations.coverage.save}
              </CustomButton>
            </div>
          </section>
        </div>
      )}
      <CustomFormDrawer
        translations={translations.drawers[drawerKey]}
        isOpened={Boolean(activeDrawer)}
        isInitialValid={Boolean(activeDrawer?.item?.id)}
        initialData={getDrawerInitialData(
          activeDrawer?.listKey,
          activeDrawer?.item
        )}
        fields={drawerFields}
        onClose={handleCloseDrawer}
        onSave={handleSaveDrawer}
        hideDelete
        hasCancelButton
      />
    </PageContainer>
  );
};

SuccessionSettingsPage.propTypes = {
  accessControl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SuccessionSettingsPage);
